/**
 * Shared origin-destination and trip type utilities
 * Used by both the Vuex store and booking engine components
 */

// Helper to strip HTML tags from a string
const strip_html = (value) => (value ? value.replace(/<[^>]*>?/gm, "") : "");

// Helper to format a Date object as YYYY-MM-DD (local time, not UTC)
const format_date = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Helper to get current date in YYYY-MM-DD format
const get_today_date = () => format_date(new Date());

// Helper to get the next day with the correct format
export const get_next_date = (previous_date) => {
  const date = new Date(previous_date + "T00:00:00");
  date.setDate(date.getDate() + 1);
  return format_date(date);
};

/**
 * Trip type enumeration
 */
export const TRIP_TYPES = {
  ROUND_TRIP: "round_trip",
  ONE_WAY: "one_way",
  MULTI_ITINERARY: "multi_itinerary",
};

/**
 * Trip type options with labels
 */
export const trip_types = [
  {
    value: TRIP_TYPES.ONE_WAY,
    label: "Solo andata",
    min_origin_destinations: 1,
  },
  {
    value: TRIP_TYPES.ROUND_TRIP,
    label: "Andata e ritorno",
    min_origin_destinations: 2,
  },
  {
    value: TRIP_TYPES.MULTI_ITINERARY,
    label: "Più destinazioni",
    min_origin_destinations: 3,
  },
];

/**
 * Create a location object
 * @param {Object} options - Optional fields to prepopulate
 * @param {string} options.iata_code - Location IATA code
 * @param {string} options.label - Location full name (HTML will be stripped)
 * @returns {Object} Location object
 */
export const create_location = (options = {}) => ({
  iata_code: options.iata_code || "",
  label: strip_html(options.label || ""),
});

/**
 * Sanitize a location value of any shape into a proper {iata_code, label} object.
 * Handles all known corruption cases from localStorage and API data:
 * - falsy (null, undefined, "") → empty location
 * - Array (e.g. ["FCO"]) → first element as iata_code
 * - string (e.g. "FCO") → treat as iata_code
 * - object with Array inner fields (e.g. {iata_code: ["FCO"]}) → extract first element
 * - valid object → pass through
 *
 * @param {*} location - Location value in any shape
 * @returns {Object} Normalized {iata_code, label} object
 */
export const sanitize_location = (location) => {
  if (!location) {
    return create_location();
  }
  if (Array.isArray(location)) {
    return create_location({ iata_code: location[0] });
  }
  if (typeof location === "string") {
    return create_location({ iata_code: location });
  }
  if (typeof location !== "object") {
    return create_location();
  }
  return create_location({
    iata_code: Array.isArray(location.iata_code)
      ? location.iata_code[0] || ""
      : location.iata_code || "",
    label: Array.isArray(location.label)
      ? location.label[0] || ""
      : location.label || "",
  });
};

/**
 * Sanitize a date value, handling Array corruption.
 * - Array (e.g. ["2026-03-15"]) → first element
 * - falsy → empty string
 * - string → pass through
 *
 * @param {*} date - Date value in any shape
 * @returns {string} Normalized date string or ""
 */
export const sanitize_date = (date) => {
  if (Array.isArray(date)) {
    return date[0] || "";
  }
  return date || "";
};

/**
 * Sanitize an origin-destination object, normalizing departure, arrival, and date.
 * @param {Object} origin_destination - Origin-destination in any shape
 * @returns {Object} Normalized {departure, arrival, date} object
 */
export const sanitize_origin_destination = (origin_destination) => {
  if (!origin_destination || typeof origin_destination !== "object") {
    return create_origin_destination();
  }
  return {
    departure: sanitize_location(origin_destination.departure),
    arrival: sanitize_location(origin_destination.arrival),
    date: sanitize_date(origin_destination.date) || get_today_date(),
  };
};

/**
 * Create an origin-destination object
 * @param {Object} options - Optional fields to prepopulate
 * @param {Object} options.departure - Departure location { iata_code, label }
 * @param {Object} options.arrival - Arrival location { iata_code, label }
 * @param {string} options.date - Date in YYYY-MM-DD format
 * @returns {Object} Origin-destination object
 */
export const create_origin_destination = (options = {}) => ({
  departure: create_location(options.departure),
  arrival: create_location(options.arrival),
  date: options.date || get_today_date(),
});

/**
 * Flip departure and arrival locations in an origin-destination
 * @param {Object} origin_destination - OD object to flip
 * @returns {Object} New OD object with departure and arrival swapped
 */
export const flip_origin_destination = (origin_destination) => ({
  ...origin_destination,
  departure: origin_destination.arrival
    ? { ...origin_destination.arrival }
    : create_location(),
  arrival: origin_destination.departure
    ? { ...origin_destination.departure }
    : create_location(),
});

/**
 * Reset origin_destinations array to match the given trip_type.
 * - ONE_WAY: keeps only the first leg
 * - ROUND_TRIP / MULTI_ITINERARY: keeps first leg + adds a flipped return leg
 * @param {Array} origin_destinations - Current origin_destinations array
 * @param {string} trip_type - One of TRIP_TYPES values
 * @returns {Array} Corrected origin_destinations array
 */
export function reset_origin_destinations_for_trip_type(
  origin_destinations,
  trip_type,
) {
  if (
    !origin_destinations ||
    !Array.isArray(origin_destinations) ||
    origin_destinations.length === 0
  ) {
    return [];
  }
  const reset = [origin_destinations[0]];
  if (trip_type !== TRIP_TYPES.ONE_WAY) {
    const { date: departure_date } = origin_destinations[0];
    const flipped = flip_origin_destination(origin_destinations[0]);
    // Sanity check to avoid same date returns as default
    if (departure_date && flipped.date && departure_date === flipped.date) {
      flipped.date = get_next_date(flipped.date);
    }
    reset.push(flipped);
  }
  return reset;
}

/**
 * Add a new origin-destination to the array
 * @param {Array} origin_destinations - Current array of OD objects
 * @param {Object} new_origin_destination - New OD to add, by default creates new one
 * @returns {Array} Updated array with new OD appended
 */
export const add_origin_destination = (
  origin_destinations,
  new_origin_destination = undefined,
) => [
  ...origin_destinations,
  new_origin_destination || create_origin_destination(),
];

/**
 * Update a specific origin-destination by index
 * @param {Array} origin_destinations - Current array of OD objects
 * @param {number} index - Index of OD to update
 * @param {Object} changes - Fields to update
 * @param {boolean} return_flight - If true, sync return leg for round-trip
 * @returns {Array} Updated array
 */
export const update_origin_destination_by_index = (
  origin_destinations,
  index,
  changes,
  return_flight = false,
) => {
  const updated = [...origin_destinations];
  updated[index] = { ...updated[index], ...changes };

  // For round-trip, sync return leg locations (reversed)
  if (return_flight && index === 0 && updated.length > 1) {
    if (changes.departure !== undefined) {
      updated[1] = { ...updated[1], arrival: { ...changes.departure } };
    }
    if (changes.arrival !== undefined) {
      updated[1] = { ...updated[1], departure: { ...changes.arrival } };
    }
  }

  return updated;
};

/**
 * Remove a specific origin-destination by index
 * @param {Array} origin_destinations - Current array of OD objects
 * @param {number} index - Index of OD to remove
 * @returns {Array} Updated array without the removed OD
 */
export const remove_origin_destination_by_index = (
  origin_destinations,
  index,
) => origin_destinations.filter((_, i) => i !== index);

/**
 * Remove multiple origin-destinations by indices
 * @param {Array} origin_destinations - Current array of OD objects
 * @param {Array} indices - Array of indices to remove
 * @returns {Array} Updated array without the removed ODs
 */
export const remove_origin_destinations_by_indices = (
  origin_destinations,
  indices,
) => {
  const indices_set = new Set(indices);
  return origin_destinations.filter((_, i) => !indices_set.has(i));
};

/**
 * Update dates for all origin-destinations
 * @param {Array} origin_destinations - Array of OD objects
 * @param {Array} dates - Array of date strings (YYYY-MM-DD)
 * @returns {Array} Updated array with new dates
 */
export const update_all_dates = (origin_destinations, dates) =>
  origin_destinations.map((od, i) => ({
    ...od,
    date: dates[i] || od.date,
  }));

/**
 * Validate if return date is after or equal to departure date
 * @param {string} departure_date - Departure date (YYYY-MM-DD)
 * @param {string} return_date - Return date (YYYY-MM-DD)
 * @returns {boolean} True if valid order
 */
export const validate_date_order = (departure_date, return_date) => {
  if (!departure_date || !return_date) return true;
  return return_date >= departure_date;
};

/**
 * Get origin-destination by index (safe accessor)
 * @param {Array} origin_destinations - Array of OD objects
 * @param {number} index - Index to retrieve
 * @returns {Object|null} OD object or null if not found
 */
export const get_origin_destination_by_index = (origin_destinations, index) =>
  origin_destinations[index] || null;

/**
 * Check if origin-destination has all required fields with truthy values
 * @param {Object} od - OD object
 * @returns {boolean} True if all required fields are truthy
 */
export const is_origin_destination_valid = ({
  departure,
  arrival,
  date,
} = {}) =>
  !!(departure && departure.iata_code && arrival && arrival.iata_code && date);

/**
 * Infer trip type from a list of itineraries
 * @param {Array} itineraries - Array of itinerary objects with segments
 * @returns {string} One of TRIP_TYPES values
 */
export const get_trip_type_from_itineraries = (itineraries) => {
  if (!itineraries || itineraries.length === 0) {
    return TRIP_TYPES.ONE_WAY;
  }
  if (itineraries.length === 1) {
    return TRIP_TYPES.ONE_WAY;
  }
  if (itineraries.length === 2) {
    const { segments: first_segments = [] } = itineraries[0];
    const { segments: last_segments = [] } = itineraries[itineraries.length - 1];
    const { departure: first_departure = {} } = first_segments[0] || {};
    const { arrival: last_arrival = {} } =
      last_segments[last_segments.length - 1] || {};
    if (first_departure.iataCode && first_departure.iataCode === last_arrival.iataCode) {
      return TRIP_TYPES.ROUND_TRIP;
    }
  }
  return TRIP_TYPES.MULTI_ITINERARY;
};
